import React from 'react';
import { Card, Form, DatePicker, Space, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TeamInsightsFilters } from './types';

dayjs.extend(utc);

interface Props {
    value: TeamInsightsFilters;
    onChange: (next: TeamInsightsFilters) => void;
}

const { RangePicker } = DatePicker;

interface Preset {
    key: string;
    label: string;
    /** Returns [from, to]. "Current" presets cap `to` at now; "Last" presets use the full period. */
    range: () => [Dayjs, Dayjs];
}

/** Today is excluded from this dashboard, so all presets clamp `to` to end-of-yesterday UTC. */
const yesterdayEndUtc = (): Dayjs => dayjs.utc().startOf('day').subtract(1, 'millisecond');
const now = yesterdayEndUtc;

// Quarters: Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec.
const quarterStart = (d: Dayjs): Dayjs => d.month(Math.floor(d.month() / 3) * 3).startOf('month');
const quarterEnd = (d: Dayjs): Dayjs => quarterStart(d).add(3, 'month').subtract(1, 'millisecond');

// Halves: H1=Jan-Jun, H2=Jul-Dec.
const halfStart = (d: Dayjs): Dayjs => d.month(d.month() < 6 ? 0 : 6).startOf('month');
const halfEnd = (d: Dayjs): Dayjs => halfStart(d).add(6, 'month').subtract(1, 'millisecond');

export const PRESETS: Preset[] = [
    { key: 'current-month', label: 'Current month', range: () => [now().startOf('month'), now()] },
    { key: 'last-month', label: 'Last month', range: () => [now().subtract(1, 'month').startOf('month'), now().subtract(1, 'month').endOf('month')] },
    { key: 'current-quarter', label: 'Current quarter', range: () => [quarterStart(now()), now()] },
    { key: 'last-quarter', label: 'Last quarter', range: () => {
        const prev = quarterStart(now()).subtract(1, 'millisecond');
        return [quarterStart(prev), quarterEnd(prev)];
    } },
    { key: 'current-half', label: 'Current half', range: () => [halfStart(now()), now()] },
    { key: 'last-half', label: 'Last half', range: () => {
        const prev = halfStart(now()).subtract(1, 'millisecond');
        return [halfStart(prev), halfEnd(prev)];
    } },
    { key: 'current-year', label: 'Current year', range: () => [now().startOf('year'), now()] },
    { key: 'last-year', label: 'Last year', range: () => [now().subtract(1, 'year').startOf('year'), now().subtract(1, 'year').endOf('year')] },
];

export const DEFAULT_PRESET_KEY = 'current-month';

export const resolvePreset = (key: string | null): Preset | null =>
    key ? PRESETS.find((p) => p.key === key) ?? null : null;

const FilterBar: React.FC<Props> = ({ value, onChange }) => {
    const range: [Dayjs | null, Dayjs | null] = [
        value.from ? dayjs(value.from) : null,
        value.to ? dayjs(value.to) : null,
    ];

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Form layout="vertical">
                <Space wrap size="middle" align="end">
                    <Form.Item label="Quick range" style={{ minWidth: 200, marginBottom: 0 }}>
                        <Select
                            value={value.preset ?? undefined}
                            onChange={(key) => onChange({ preset: key, from: null, to: null })}
                            options={PRESETS.map((p) => ({ label: p.label, value: p.key }))}
                            style={{ minWidth: 200 }}
                        />
                    </Form.Item>

                    <Form.Item label="Custom window" style={{ marginBottom: 0 }}>
                        <RangePicker
                            showTime
                            value={range as any}
                            onChange={(dates) => {
                                onChange({
                                    preset: null,
                                    from: dates?.[0]?.toISOString() ?? null,
                                    to: dates?.[1]?.toISOString() ?? null,
                                });
                            }}
                        />
                    </Form.Item>
                </Space>
            </Form>
        </Card>
    );
};

export default FilterBar;
